/**
 * Closed-set JSON Schema ⇄ decisions-API questions. Pure: no fetch, no storage.
 *
 * A decisions model answers QUESTIONS, not schemas: a `choice` picks one option, a
 * `noul` returns the probability that a yes/no statement is true, and a `score`
 * places an answer on an ordered ladder of levels. An agent's `schema_json`
 * already says which of those each field is, so `schemaToQuestions` reads it and
 * `answersToObject` folds the answers back into the JSON object that schema
 * describes, plus one confidence value per field.
 *
 * A schema this module refuses is a schema a decisions model cannot answer, which
 * is why the refusals are part of the result rather than an error message: a
 * caller gating a model binding asks `ok`, a caller reporting to an admin reads
 * `refusals`.
 *
 * The three primitives and their wire fields are the decisions API's; the mapping
 * from a JSON Schema property to one of them is this module's.
 */

/**
 * The suffix that names a field's confidence beside the field itself, so
 * `urgency` and `urgency_confidence` are one pair a condition can compare.
 */
export const CONFIDENCE_VARIABLE_SUFFIX = '_confidence';

/** A score ladder needs at least two levels, and the API accepts at most ten. */
export const DECISION_SCORE_MIN_LEVELS = 2;
export const DECISION_SCORE_MAX_LEVELS = 10;

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

export interface DecisionChoiceQuestion {
  type: 'choice';
  instructions: string;
  /** Option name → its description. */
  criteria: Record<string, string>;
}

export interface DecisionScoreQuestion {
  type: 'score';
  instructions: string;
  /** Level descriptions, ordered from the low end of the scale to the high end. */
  criteria: string[];
}

export interface DecisionNoulQuestion {
  type: 'noul';
  instructions: string;
}

export type DecisionQuestion = DecisionChoiceQuestion | DecisionScoreQuestion | DecisionNoulQuestion;

export interface DecisionChoiceAnswer {
  type: 'choice';
  choice: string;
  confidence?: number;
}

export interface DecisionScoreAnswer {
  type: 'score';
  /** A position on the question's `criteria`; see `scoreLevelIndex`. */
  score: number;
  confidence?: number;
}

export interface DecisionNoulAnswer {
  type: 'noul';
  /** Probability that the answer is yes, 0 to 1. */
  noul: number;
}

export type DecisionAnswer = DecisionChoiceAnswer | DecisionScoreAnswer | DecisionNoulAnswer;

// ---------------------------------------------------------------------------
// Schema → questions
// ---------------------------------------------------------------------------

/**
 * Why a schema, or one of its fields, cannot become a question.
 *
 * `schema_not_object` / `schema_has_no_properties` — the schema itself, not a field.
 * `unsupported_type` — a string, number, array, object or untyped field: free text,
 *   arithmetic and nesting are what a decisions model does not do.
 * `too_few_options` — fewer than two options or levels, so there is nothing to decide.
 * `integer_not_bounded` — an integer without both `minimum` and `maximum`: a score
 *   ladder needs to know how many levels it has.
 * `integer_range_too_large` — more levels than the API accepts.
 */
export type DecisionQuestionRefusalReason =
  | 'schema_not_object'
  | 'schema_has_no_properties'
  | 'unsupported_type'
  | 'too_few_options'
  | 'integer_not_bounded'
  | 'integer_range_too_large';

export interface DecisionQuestionRefusal {
  /** The offending property, or null when the schema itself is refused. */
  field: string | null;
  reason: DecisionQuestionRefusalReason;
}

export type SchemaToQuestionsResult =
  | { ok: true; questions: Record<string, DecisionQuestion> }
  | { ok: false; refusals: DecisionQuestionRefusal[] };

/**
 * Turn a closed-set object schema into the decisions API's `questions` map.
 *
 * Every property has to map, and the result names every one that did not rather
 * than the first: an admin fixing a schema wants the whole list, and a binding
 * check wants a reason it can show.
 */
export function schemaToQuestions(schema: unknown): SchemaToQuestionsResult {
  const classified = classifySchema(schema);
  if (classified.refusals.length > 0) return { ok: false, refusals: classified.refusals };

  const questions: Record<string, DecisionQuestion> = {};
  for (const [field, primitive] of classified.fields) {
    questions[field] = toQuestion(primitive);
  }
  return { ok: true, questions };
}

// ---------------------------------------------------------------------------
// Answers → object
// ---------------------------------------------------------------------------

export interface AnswersToObjectResult {
  /** The JSON object the schema describes. */
  values: Record<string, string | number | boolean>;
  /** One entry per answered field, keyed `<field>${CONFIDENCE_VARIABLE_SUFFIX}`. */
  confidence: Record<string, number>;
}

/**
 * Fold a decisions-API `answers` map back into the object its schema describes.
 *
 * A field whose answer is missing, of the wrong primitive, or outside what the
 * schema admits is LEFT OUT rather than defaulted: an absent variable is a
 * condition that does not fire, while a fabricated one is a decision nobody made.
 * A schema with any refusal folds nothing at all — answers to questions that
 * could not all be asked describe an object this schema does not.
 */
export function answersToObject(
  schema: unknown,
  answers: Record<string, DecisionAnswer | undefined> | null | undefined,
): AnswersToObjectResult {
  const values: Record<string, string | number | boolean> = {};
  const confidence: Record<string, number> = {};

  const classified = classifySchema(schema);
  if (classified.refusals.length > 0 || !isRecord(answers)) return { values, confidence };

  for (const [field, primitive] of classified.fields) {
    const answer = answers[field];
    if (!answer) continue;

    if (primitive.kind === 'choice' && answer.type === 'choice') {
      if (!primitive.options.includes(answer.choice)) continue;
      values[field] = answer.choice;
      setConfidence(confidence, field, answer.confidence);
      continue;
    }

    if (primitive.kind === 'noul' && answer.type === 'noul') {
      if (!isProbability(answer.noul)) continue;
      values[field] = answer.noul >= 0.5;
      // A noul answer returns only the probability, and that probability
      // describes the whole binary distribution — so its confidence is how far
      // the answer sits from an even split, on the same 0-to-1 scale the other
      // two primitives report.
      setConfidence(confidence, field, Math.abs(answer.noul - 0.5) * 2);
      continue;
    }

    if (primitive.kind === 'score' && answer.type === 'score') {
      if (!Number.isFinite(answer.score)) continue;
      values[field] = primitive.minimum + scoreLevelIndex(answer.score, primitive.levels);
      setConfidence(confidence, field, answer.confidence);
      continue;
    }
  }

  return { values, confidence };
}

/**
 * Which level an answered `score` names.
 *
 * `score` is a POSITION on the `criteria` array counted from 0, so level 0 is the
 * schema's `minimum`. The vendor's published example for a score question answers
 * three criteria with a `legend` keyed `"0"`, `"1"`, `"2"`; the base is stated
 * here once, and `scoreLevels` labels the array on the same reading, which is what
 * keeps a label and a decoded value describing the same thing. If a live response
 * ever shows another base, both move together.
 *
 * A score is probability-weighted and so lands between levels. The schema's field
 * is an integer, so the NEAREST level is taken, halves rounding up, and the result
 * is clamped to the ladder the schema declares.
 */
function scoreLevelIndex(score: number, levels: number): number {
  return Math.min(levels - 1, Math.max(0, Math.round(score)));
}

// ---------------------------------------------------------------------------
// Schema classification — the one place a property's primitive is decided
// ---------------------------------------------------------------------------

type ClassifiedPrimitive =
  | { kind: 'choice'; instructions: string; options: string[] }
  | { kind: 'noul'; instructions: string }
  | { kind: 'score'; instructions: string; minimum: number; levels: number };

interface ClassifiedSchema {
  /** Field → its primitive, in the schema's own property order. Empty when anything was refused. */
  fields: Map<string, ClassifiedPrimitive>;
  /** Empty when every property mapped. */
  refusals: DecisionQuestionRefusal[];
}

/**
 * Classify a whole object schema once.
 *
 * Both entry points read this rather than each deriving their own: a field
 * classified twice could be asked as one primitive and decoded as another, and
 * `answersToObject` would need to re-assert a shape `schemaToQuestions` had
 * already established.
 *
 * Every property has to map, and the refusals name every one that did not rather
 * than the first: an admin fixing a schema wants the whole list, and a binding
 * check wants a reason it can show.
 */
function classifySchema(schema: unknown): ClassifiedSchema {
  const fields = new Map<string, ClassifiedPrimitive>();

  if (!isRecord(schema) || schema.type !== 'object') {
    return { fields, refusals: [{ field: null, reason: 'schema_not_object' }] };
  }

  const properties = schema.properties;
  if (!isRecord(properties) || Object.keys(properties).length === 0) {
    return { fields, refusals: [{ field: null, reason: 'schema_has_no_properties' }] };
  }

  const refusals: DecisionQuestionRefusal[] = [];
  for (const [field, property] of Object.entries(properties)) {
    const primitive = classifyField(field, property);
    if ('reason' in primitive) {
      refusals.push({ field, reason: primitive.reason });
      continue;
    }
    fields.set(field, primitive);
  }

  return refusals.length > 0 ? { fields: new Map(), refusals } : { fields, refusals };
}

/**
 * Which primitive a property is, if any.
 *
 * A string `enum` is a choice, a boolean is a noul, and a bounded integer is a
 * score ladder over its own values. Nothing else maps: a free string, an unbounded
 * number, an array and a nested object are all things a decisions model has no
 * answer shape for.
 *
 * JSON Schema has no standard way to say that an enum's members are ordered, so an
 * ordered set of labels reaches a score ladder only as a bounded integer.
 */
function classifyField(
  field: string,
  property: unknown,
): ClassifiedPrimitive | { reason: DecisionQuestionRefusalReason } {
  if (!isRecord(property)) return { reason: 'unsupported_type' };

  const instructions = describeField(field, property);

  if (property.enum !== undefined) {
    const declared = property.enum;
    if (!Array.isArray(declared) || declared.some((option) => typeof option !== 'string')) {
      return { reason: 'unsupported_type' };
    }
    // Counted after de-duplication, because the wire shape is a map keyed by
    // option name: `enum: ['yes', 'yes']` is two entries here and ONE option
    // there, which is the choice-with-nothing-to-decide this guard exists for.
    // The same de-duplicated list is what an answer is checked against.
    const options = [...new Set(declared as string[])];
    if (options.length < 2) return { reason: 'too_few_options' };
    return { kind: 'choice', instructions, options };
  }

  if (property.type === 'boolean') return { kind: 'noul', instructions };

  if (property.type === 'integer') {
    const minimum = property.minimum;
    const maximum = property.maximum;
    if (!Number.isInteger(minimum) || !Number.isInteger(maximum)) {
      return { reason: 'integer_not_bounded' };
    }
    const levels = (maximum as number) - (minimum as number) + 1;
    if (levels < DECISION_SCORE_MIN_LEVELS) return { reason: 'too_few_options' };
    if (levels > DECISION_SCORE_MAX_LEVELS) return { reason: 'integer_range_too_large' };
    return { kind: 'score', instructions, minimum: minimum as number, levels };
  }

  return { reason: 'unsupported_type' };
}

/** The wire question a classified property asks. */
function toQuestion(primitive: ClassifiedPrimitive): DecisionQuestion {
  switch (primitive.kind) {
    case 'choice':
      return {
        type: 'choice',
        instructions: primitive.instructions,
        // A JSON Schema enum carries no per-option text, so each option stands as
        // its own description. The distinguishing detail an option needs belongs
        // in the field's `description`, which is the instructions.
        criteria: Object.fromEntries(primitive.options.map((option) => [option, option])),
      };
    case 'noul':
      return { type: 'noul', instructions: primitive.instructions };
    case 'score':
      return {
        type: 'score',
        instructions: primitive.instructions,
        criteria: scoreLevels(primitive.minimum, primitive.levels),
      };
  }
}

/** The field's own `description`, or its name when it carries none. */
function describeField(field: string, property: unknown): string {
  const description = isRecord(property) ? property.description : undefined;
  return typeof description === 'string' && description.trim() !== '' ? description : field;
}

/**
 * Level descriptions for a bounded integer: the values it admits, in order, so the
 * label at a level's position reads as the value `scoreLevelIndex` decodes it to.
 */
function scoreLevels(minimum: number, levels: number): string[] {
  return Array.from({ length: levels }, (_, index) => String(minimum + index));
}

function setConfidence(into: Record<string, number>, field: string, value: unknown): void {
  if (!isProbability(value)) return;
  into[`${field}${CONFIDENCE_VARIABLE_SUFFIX}`] = value;
}

function isProbability(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
